import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  addFileToMessageTypes,
  messageTypes,
  sendMessageTypes,
} from "./messageTypes";
import messageService from "./messageService";

interface messageState {
  id_message: number | null;
  message: messageTypes | null;
  messages: messageTypes[] | null;
  loading: boolean;
  error: any | null;
}

const initialState: messageState = {
  id_message: null,
  message: null,
  messages: null,
  loading: false,
  error: null,
};

export const sendMessageAuthor = createAsyncThunk<
  any,
  sendMessageTypes,
  { rejectValue: string }
>("messages/sendMessageAuthor", async (data, { rejectWithValue }) => {
  try {
    const response = await messageService.sendMessageAuthor(data);
    return response;
  } catch (error: any) {
    return rejectWithValue(error?.message || "Error al enviar mensaje");
  }
});

export const getMessagesAuthor = createAsyncThunk<
  messageTypes[],
  string,
  { rejectValue: string }
>("messages/getMessagesAuthor", async (slug: string, { rejectWithValue }) => {
  try {
    const messages = await messageService.getMessagesAuthor(slug);
    return messages;
  } catch (error: any) {
    return rejectWithValue(error?.message || "Error al obtener mensajes");
  }
});

export const addFileToMessageAuthor = createAsyncThunk<
  string,
  addFileToMessageTypes,
  { rejectValue: string }
>(
  "messages/addFileToMessageAuthor",
  async (data: addFileToMessageTypes, { rejectWithValue }) => {
    try {
      const response = await messageService.addFileToMessageAuthor(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Error al subir archivo");
    }
  },
);

export const respond_to_galley_author = createAsyncThunk<
  any,
  { article_slug: string; payload: { status: number; message: string }[] },
  { rejectValue: string }
>(
  "messages/respond_to_galley_author",
  async ({ article_slug, payload }, { rejectWithValue }) => {
    try {
      const response = await messageService.respond_to_galley_author({
        article_slug,
        payload,
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Error al enviar mensaje");
    }
  },
);

export const addFileToGalley = createAsyncThunk<
  string,
  addFileToMessageTypes,
  { rejectValue: string }
>(
  "messages/addFileToGalley",
  async (data: addFileToMessageTypes, { rejectWithValue }) => {
    try {
      const response = await messageService.addFileToGalley(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Error al subir archivo");
    }
  },
);

export const respond_to_galley_editor = createAsyncThunk<
  any,
  { article_slug: string; payload: { status: number; message: string }[] },
  { rejectValue: string }
>(
  "messages/respond_to_galley_editor",
  async ({ article_slug, payload }, { rejectWithValue }) => {
    try {
      const response = await messageService.respond_to_galley_editor({
        article_slug,
        payload,
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Error al enviar mensaje");
    }
  },
);

export const addFileToEditorGalley = createAsyncThunk<
  string,
  addFileToMessageTypes,
  { rejectValue: string }
>(
  "messages/addFileToEditorGalley",
  async (data: addFileToMessageTypes, { rejectWithValue }) => {
    try {
      const response = await messageService.addFileToEditorGalley(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Error al subir archivo");
    }
  },
);

export const sendMessageEditor = createAsyncThunk<
  any,
  sendMessageTypes,
  { rejectValue: string }
>("messages/sendMessageEditor", async (data, { rejectWithValue }) => {
  try {
    const response = await messageService.sendMessageEditor(data);
    return response;
  } catch (error: any) {
    return rejectWithValue(error?.message || "Error al enviar mensaje");
  }
});

export const getMessagesEditor = createAsyncThunk<
  messageTypes[],
  string,
  { rejectValue: string }
>("messages/getMessagesEditor", async (slug: string, { rejectWithValue }) => {
  try {
    const messages = await messageService.getMessagesEditor(slug);
    return messages;
  } catch (error: any) {
    return rejectWithValue(error?.message || "Error al obtener mensajes");
  }
});

export const addFileToMessageEditor = createAsyncThunk<
  string,
  addFileToMessageTypes,
  { rejectValue: string }
>(
  "messages/addFileToMessageEditor",
  async (data: addFileToMessageTypes, { rejectWithValue }) => {
    try {
      const response = await messageService.addFileToMessageEditor(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Error al subir archivo");
    }
  },
);

export const sendMessageToVol = createAsyncThunk<
  any,
  { volume_slug: string; message: string },
  { rejectValue: string }
>("messages/sendMessageToVol", async (data, { rejectWithValue }) => {
  try {
    const response = await messageService.sendMessageToVol(data);
    return response;
  } catch (error: any) {
    return rejectWithValue(error?.message || "Error al enviar mensaje");
  }
});

export const getVolumeMessages = createAsyncThunk<
  messageTypes[],
  string,
  { rejectValue: string }
>("messages/getVolumeMessages", async (slug: string, { rejectWithValue }) => {
  try {
    const messages = await messageService.getVolumeMessages(slug);
    return messages;
  } catch (error: any) {
    return rejectWithValue(error?.message || "Error al obtener mensajes");
  }
});

export const getGalleyHistory = createAsyncThunk<
  messageTypes[],
  string,
  { rejectValue: string }
>("messages/getGalleyHistory", async (slug: string, { rejectWithValue }) => {
  try {
    const messages = await messageService.getGalleyHistory(slug);
    return messages;
  } catch (error: any) {
    return rejectWithValue(error?.message || "Error al obtener mensajes");
  }
});

const messageSlice = createSlice({
  name: "message",
  initialState,
  reducers: {
    reset_id_message: (state) => {
      state.id_message = null;
    },
    reset_message: (state) => {
      state.message = null;
    },
    reset_messages: (state) => {
      state.messages = null;
    },
    reset_error: (state) => {
      state.error = null;
    },
    reset_loading: (state) => {
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    // Author cases
    builder
      .addCase(sendMessageAuthor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMessageAuthor.fulfilled, (state, action) => {
        state.loading = false;
        state.id_message = action.payload;
      })
      .addCase(sendMessageAuthor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getMessagesAuthor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMessagesAuthor.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload;
      })
      .addCase(getMessagesAuthor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(addFileToMessageAuthor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addFileToMessageAuthor.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addFileToMessageAuthor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Editor cases
      .addCase(sendMessageEditor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMessageEditor.fulfilled, (state, action) => {
        state.loading = false;
        state.id_message = action.payload;
      })
      .addCase(sendMessageEditor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getMessagesEditor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMessagesEditor.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload;
      })
      .addCase(getMessagesEditor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ CORREGIDO: Usar el addFileToMessageEditor correctamente
      .addCase(addFileToMessageEditor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addFileToMessageEditor.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addFileToMessageEditor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(sendMessageToVol.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMessageToVol.fulfilled, (state, action) => {
        state.loading = false;
        state.id_message = action.payload;
      })
      .addCase(sendMessageToVol.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getVolumeMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVolumeMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload;
      })
      .addCase(getVolumeMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ✅ Agregado el caso para getGalleyHistory
      .addCase(getGalleyHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getGalleyHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload;
      })
      .addCase(getGalleyHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      //respond_to_galley_author
      .addCase(respond_to_galley_author.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(respond_to_galley_author.fulfilled, (state, action) => {
        state.loading = false;
        state.id_message = action.payload;
      })
      .addCase(respond_to_galley_author.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      //respond_to_galley_editor
      .addCase(respond_to_galley_editor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(respond_to_galley_editor.fulfilled, (state, action) => {
        state.loading = false;
        state.id_message = action.payload;
      })
      .addCase(respond_to_galley_editor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  reset_id_message,
  reset_message,
  reset_messages,
  reset_error,
  reset_loading,
} = messageSlice.actions;

export default messageSlice.reducer;
